import { useState } from "react"
import { cn } from "@/lib/utils"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../Sheet/Sheet"

export interface MonthlyReturnsYear {
  year: number
  months: Array<number | null>
}

export interface MonthlyReturnsHeatmapProps {
  years: MonthlyReturnsYear[]
  title?: string
  rangeLabel?: string
  highlight?: { year: number; monthIndex: number } | null
  monthLabels?: string[]
  className?: string
}

const DEFAULT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

function formatPct(v: number | null) {
  if (v === null) return "—"
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`
}

function pctColor(v: number | null) {
  if (v === null) return "text-muted-foreground/40"
  if (v > 0) return "text-success"
  if (v < 0) return "text-danger"
  return "text-muted-foreground"
}

function yearTotal(year: MonthlyReturnsYear) {
  return year.months.reduce<number>((s, v) => s + (v ?? 0), 0)
}

function yearHasData(year: MonthlyReturnsYear) {
  return year.months.some((v) => v !== null)
}

const HEAD_CELL =
  "text-caption font-medium text-muted-foreground px-2 py-1.5 text-right uppercase tracking-wider"

export function MonthlyReturnsHeatmap({
  years,
  title = "Monthly Returns",
  rangeLabel,
  highlight = null,
  monthLabels = DEFAULT_MONTHS,
  className,
}: MonthlyReturnsHeatmapProps) {
  const [drawerYear, setDrawerYear] = useState<MonthlyReturnsYear | null>(null)

  return (
    <div className={cn("border-b border-border px-4 py-4", className)}>
      <div className="flex flex-col gap-1 mb-3">
        <div className="text-section text-foreground">{title}</div>
        {rangeLabel && (
          <div className="text-caption text-muted-foreground">{rangeLabel}</div>
        )}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-caption">
          <thead>
            <tr className="border-b border-border">
              <th className={cn(HEAD_CELL, "text-left w-12")}>Year</th>
              {monthLabels.map((m) => (
                <th key={m} className={HEAD_CELL}>
                  {m}
                </th>
              ))}
              <th className={HEAD_CELL}>Total</th>
            </tr>
          </thead>
          <tbody>
            {years.map((yr) => {
              const total = yearTotal(yr)
              const hasData = yearHasData(yr)
              return (
                <tr key={yr.year} className="border-b border-border last:border-b-0">
                  <td className="text-caption font-medium text-foreground px-2 py-1.5 text-left">
                    {yr.year}
                  </td>
                  {yr.months.map((v, i) => {
                    const highlighted =
                      highlight && highlight.year === yr.year && highlight.monthIndex === i
                    return (
                      <td
                        key={i}
                        className={cn(
                          "px-2 py-1.5 text-right",
                          pctColor(v),
                          highlighted && "font-medium",
                        )}
                      >
                        {formatPct(v)}
                      </td>
                    )
                  })}
                  <td
                    className={cn(
                      "px-2 py-1.5 text-right font-medium",
                      hasData ? pctColor(total) : "text-muted-foreground/40",
                    )}
                  >
                    {hasData ? formatPct(total) : "—"}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        <table className="w-full text-caption">
          <thead>
            <tr className="border-b border-border">
              <th className={cn(HEAD_CELL, "text-left w-16")}>Year</th>
              <th className={HEAD_CELL}>Total</th>
            </tr>
          </thead>
          <tbody>
            {years.map((yr) => {
              const total = yearTotal(yr)
              const hasData = yearHasData(yr)
              return (
                <tr
                  key={yr.year}
                  onClick={() => setDrawerYear(yr)}
                  className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/50 active:bg-muted transition-colors"
                >
                  <td className="text-caption font-medium text-foreground px-2 py-3 text-left">
                    {yr.year}
                  </td>
                  <td
                    className={cn(
                      "px-2 py-3 text-right font-medium",
                      hasData ? pctColor(total) : "text-muted-foreground/40",
                    )}
                  >
                    {hasData ? formatPct(total) : "—"}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Sheet
        open={drawerYear !== null}
        onOpenChange={(open) => {
          if (!open) setDrawerYear(null)
        }}
      >
        <SheetContent
          side="bottom"
          className="v1 bg-card border-border max-h-[85vh] overflow-y-auto"
        >
          {drawerYear && (
            <>
              <SheetHeader>
                <SheetTitle className="text-body text-foreground">
                  {drawerYear.year}
                </SheetTitle>
                <p className="text-caption text-muted-foreground mt-1">
                  Monthly returns
                </p>
              </SheetHeader>

              <div className="flex flex-col mt-4 px-4 pb-4">
                {drawerYear.months.map((v, i) => {
                  const highlighted =
                    highlight &&
                    highlight.year === drawerYear.year &&
                    highlight.monthIndex === i
                  return (
                    <div
                      key={i}
                      className={cn(
                        "flex justify-between border-b border-border py-2",
                        highlighted && "bg-muted/40 -mx-2 px-2 rounded",
                      )}
                    >
                      <span className="text-caption text-muted-foreground">
                        {monthLabels[i]}
                      </span>
                      <span
                        className={cn(
                          "text-body",
                          pctColor(v),
                          highlighted && "font-medium",
                        )}
                      >
                        {formatPct(v)}
                      </span>
                    </div>
                  )
                })}
                <div className="flex justify-between py-3 mt-1">
                  <span className="text-caption font-medium text-foreground">
                    Total
                  </span>
                  <span
                    className={cn(
                      "text-body font-semibold",
                      yearHasData(drawerYear)
                        ? pctColor(yearTotal(drawerYear))
                        : "text-muted-foreground/40",
                    )}
                  >
                    {yearHasData(drawerYear)
                      ? formatPct(yearTotal(drawerYear))
                      : "—"}
                  </span>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
